'use client';

import { useCallback, useContext, useRef } from 'react';
import { useSelector } from 'react-redux';
import { YouTubeEvent } from 'react-youtube';

// types
import type { ID } from 'react-accessible-accordion/dist/types/components/ItemContext';
import type {
  WebShopAddToCartData,
  WebShopLoginData,
  WebShopViewCartData,
  WebShopRemoveFromCartData,
  WebShopBeginCheckoutData,
  WebShopViewItemData,
  WebShopPurchaseData,
} from './types';
import type { FormikErrors } from 'formik';

// utils
import { Log } from 'services/log';
import { isPreviewSelector } from 'utils/selectors/environmentSelectors';
import { useTranslationFunction } from '../hooks/use-translations';
import { useContent } from 'components/Page/page-hooks';
import { NavigationContext } from 'components/Navigation/NavigationContext';

const selectFn = process.env.JEST_WORKER_ID !== undefined ? () => {} : useSelector;

export const QUEUE_TRACK_VIEW_CART = 'trackViewCart';
export const QUEUE_TRACK_ADD_TO_CART = 'trackAddToCart';

/** Adds a slash to the provided url path if not present */
export function addSlashToPath(path: string) {
  if (!path.endsWith('/')) {
    path += '/';
  }
  return path;
}

/** GA4 tracking */
export function useTracking() {
  const content = useContent();
  const navigationContext = useContext(NavigationContext);
  const isPreview = selectFn(isPreviewSelector);
  const translate = useTranslationFunction();
  // Gets the pageId from the context of the page and if this is empty, get it from the context of the navigation.
  const pageId = content?._id ?? navigationContext.content?._id;

  /** Pushes an object to the window.dataLayer array */
  const trackGA4Event = useCallback(
    (data: object) => {
      const ga4Data = { id: 'ga4Tracking', 'page-id': pageId, ...data };
      if (
        (window && ['local.web.geberit.com', 'localhost'].includes(window.location.hostname)) ||
        isPreview
      ) {
        Log.info('TRACKING EVENT', ga4Data);
      }

      const browserWindow = window as
        | undefined
        | (Window & {
            dataLayer?: object[];
          });

      if (browserWindow?.dataLayer) {
        browserWindow.dataLayer.push(ga4Data);
      }
    },
    [isPreview],
  );

  const trackScroll = useCallback(
    (percent_scrolled: number) => {
      trackGA4Event({
        event: 'scroll',
        percent_scrolled,
      });
    },
    [trackGA4Event],
  );

  const trackSearch = useCallback(
    (data: { content_type: 'e-paper portal (Oxomi)' | 'geberit site'; search_term: string }) => {
      trackGA4Event({ event: 'search', ...data });
    },
    [trackGA4Event],
  );

  const trackOxomi = useCallback(
    (data: {
      click_intent:
        | 'image click open - e-paper portal'
        | 'open pdf - e-paper portal'
        | 'download pdf';
      file_name: string;
      file_extension: 'pdf';
      value: number;
    }) => {
      trackGA4Event({ event: 'file_download', ...data });
    },
    [trackGA4Event],
  );

  const trackShareAction = useCallback(
    (data: { content_type: 'page' | 'video'; method: string; shareUrl?: string }) => {
      trackGA4Event({
        event: 'share',
        value: data.content_type === 'page' ? 10 : 0,
        ...data,
      });
    },
    [trackGA4Event],
  );

  const trackClick = useCallback(
    (data: {
      click_intent: string;
      click_element?: string;
      click_url?: string;
      click_text?: string;
      [key: string]: string | number | undefined;
    }) => {
      const clickEventData = { event: 'click' };
      trackGA4Event({ ...clickEventData, ...data });
    },
    [trackGA4Event],
  );

  const trackEvent = useCallback(
    (data: {
      event: string;
      click_intent: string;
      click_element?: string;
      click_url?: string;
      click_text?: string;
      [key: string]: string | number | undefined;
    }) => {
      trackGA4Event({ ...data });
    },
    [trackGA4Event],
  );

  const trackLocator = useCallback(
    (data: {
      click_intent: string;
      search_term: string;
      value?: number;
      currency?: string;
      search_term_executed?: string;
      content_type?: string;
    }) => {
      const clickEventData: Record<string, string> = {
        event: 'locator_interaction',
        click_element: 'locator',
      };
      if (data.value) {
        clickEventData.currency = data.currency ?? 'EUR';
      }
      if (data.content_type) {
        clickEventData.content_type = data.content_type ?? 'locator';
      }
      trackGA4Event({ ...clickEventData, ...data });
    },
    [trackGA4Event],
  );

  const trackFormInteraction = useCallback(
    (data: {
      event: string;
      click_intent?: string;
      content_type?: string;
      click_element?: string;
      search_term?: string;
      errors?: FormikErrors<Record<string, string>>;
    }) => {
      trackGA4Event(data);
    },
    [trackGA4Event],
  );

  const trackFormSubmission = useCallback(
    (data: {
      event: string,
      content_name: string,
      content_type: 'CRM form' | 'contact',
      value: number,
    }) => {
      trackGA4Event({
        ...data,
        method: 'form',
      });
  }, [trackGA4Event]);

  const openedAccordionEntriesRef = useRef<Parameters<typeof trackAccordionOpening>[0]>([]);

  /** Call inside <Accordion onChange> from react-accessible-accordion
   * @example
   * onChange={(uuid) => trackAccordionOpening(uuid)}
   * */
  const trackAccordionOpening = useCallback(
    (uuid: ID[]) => {
      let shouldTrackOpening = false;

      // If more entries are opened than last time, it is an opening action
      if (uuid.length > openedAccordionEntriesRef.current.length) shouldTrackOpening = true;

      if (openedAccordionEntriesRef.current.length === 0) shouldTrackOpening = true;
      openedAccordionEntriesRef.current = uuid;

      if (!shouldTrackOpening) return;

      trackClick({
        click_element: 'accordion',
        click_intent: 'more_info',
        click_text: typeof uuid[0] === 'string' ? uuid[0] : undefined,
      });
    },
    [trackClick],
  );

  const trackMI24Video = useCallback(
    (ga4VideoTrackRef, playerApi, title, videoId) => {
      let videoStarted = false;

      playerApi.registerEventListener('play', () => {
        const currentTime = playerApi.getCurrentTime();
        if (currentTime === 0 && videoStarted === false) {
          trackGA4Event({
            event: 'video_start',
            video_title: title,
            video_id: videoId,
            video_provider: 'geberit video',
          });
          videoStarted = true;
        }
      });

      playerApi.registerEventListener('ended', () => {
        trackGA4Event({
          event: 'video_complete',
          video_title: title,
          video_id: videoId,
          video_provider: 'geberit video',
        });
        ga4VideoTrackRef.current = 0;
      });

      playerApi.registerEventListener('timeupdate', () => {
        const currentTime = playerApi.getCurrentTime();
        const totalDuration = playerApi.getDuration();
        const percent = Math.floor((currentTime * 100) / totalDuration);
        if (ga4VideoTrackRef.current < 25 && percent >= 25) {
          ga4VideoTrackRef.current = 25;
          trackGA4Event({
            event: 'video_progress',
            video_title: title,
            video_id: videoId,
            video_percent: ga4VideoTrackRef.current,
            video_provider: 'geberit video',
          });
        } else if (ga4VideoTrackRef.current < 50 && percent >= 50) {
          ga4VideoTrackRef.current = 50;
          trackGA4Event({
            event: 'video_progress',
            video_title: title,
            video_id: videoId,
            video_percent: ga4VideoTrackRef.current,
            video_provider: 'geberit video',
          });
        } else if (ga4VideoTrackRef.current < 75 && percent >= 75) {
          ga4VideoTrackRef.current = 75;
          trackGA4Event({
            event: 'video_progress',
            video_title: title,
            video_id: videoId,
            video_percent: ga4VideoTrackRef.current,
            video_provider: 'geberit video',
          });
        }
      });
    },
    [trackGA4Event],
  );

  const trackYoutubeVideo = useCallback(
    (
      event: YouTubeEvent<number>,
      videoId: string,
      title: string,
      isVideoStarted?: boolean,
      progress?: number,
    ) => {
      if (event.data === 1) {
        // only initial play should be tracked
        if (!isVideoStarted) {
          trackGA4Event({
            event: 'video_start',
            video_title: title,
            video_id: videoId,
            video_provider: 'geberit video',
          });
        } else if (progress) {
          trackGA4Event({
            event: 'video_progress',
            video_title: title,
            video_id: videoId,
            video_percent: progress,
            video_provider: 'geberit video',
          });
        }
      } else if (event.data === 0) {
        trackGA4Event({
          event: 'video_complete',
          video_title: title,
          video_id: videoId,
          video_provider: 'geberit video',
        });
      }
    },
    [trackGA4Event],
  );

  function trackWebshop(event: 'login', data: WebShopLoginData): void;
  function trackWebshop(event: 'begin_checkout', data: WebShopBeginCheckoutData): void;
  function trackWebshop(event: 'view_item', data: WebShopViewItemData): void;
  function trackWebshop(event: 'add_to_cart', data: WebShopAddToCartData): void;
  function trackWebshop(event: 'view_cart', data: WebShopViewCartData): void;
  function trackWebshop(event: 'remove_from_cart', data: WebShopRemoveFromCartData): void;
  function trackWebshop(event: 'purchase', data: WebShopPurchaseData): void;

  function trackWebshop(event: any, data: any) {
    const eventsWithCurrency = [
      'begin_checkout',
      'view_item',
      'add_to_cart',
      'view_cart',
      'remove_from_cart',
      'purchase',
    ];
    if (eventsWithCurrency.includes(event)) {
      const currency = translate('web20_webshop_basket_currency');
      data.currency = currency;
    }

    trackGA4Event({ event, ...data });
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedTrackWebshop = useCallback(trackWebshop, [trackGA4Event, translate]);

  return {
    trackScroll,
    trackSearch,
    trackOxomi,
    trackShareAction,
    trackClick,
    trackEvent,
    trackLocator,
    trackAccordionOpening,
    trackMI24Video,
    trackYoutubeVideo,
    trackWebshop: memoizedTrackWebshop,
    trackFormInteraction,
    trackFormSubmission,
  };
}

export type TrackClick = ReturnType<typeof useTracking>['trackClick'];
export type TrackOxomi = ReturnType<typeof useTracking>['trackOxomi'];
